import { useEffect, useMemo, useState } from 'react';
import { getAllCompositeSegments } from '@goldcast/player-config/src/utils/clipUtils';
import { convertSecondsToTimeString, getDeletesForSegmentPure } from '@goldcast/player-config/src/utils/timeUtils';
import {
  ClipDeletes,
  CompositeSegment
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useDeletesWithDragChanges from '@/components/molecules/Transcript/useDeletesWithDragChanges';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

export default function TotalCompositeDuration({ compositeSegment }: { compositeSegment: CompositeSegment }) {
  const { clipData, fps } = useClipsContext();
  const [timeChangeDueToDrag, setTimeChangeDueToDrag] = useState(0);

  const duration = compositeSegment
    ? (compositeSegment.currentBounds.toFrame - compositeSegment.currentBounds.fromFrame) / fps
    : 0;

  const allDeletes = useMemo(() => {
    return getAllCompositeSegments(clipData).reduce(
      (acc, curr) => {
        return {
          ...acc,
          [curr.id]: getDeletesForSegmentPure({
            clip: clipData,
            segmentId: curr.id,
            isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
          })
        };
      },
      {} as Record<string, ClipDeletes>
    );
  }, [clipData]);
  const { newDeletes: finalDeletes, compositeSegmentId } = useDeletesWithDragChanges({ allDeletes });

  const compositeSegmentDeletes = useMemo(() => {
    return allDeletes[compositeSegment.id];
  }, [allDeletes, compositeSegment.id]);

  const compositeSegmentFinalDeletes = useMemo(() => {
    return finalDeletes[compositeSegment.id];
  }, [finalDeletes, compositeSegment.id]);

  const previousTotalDeleteTime = useMemo(
    () =>
      Object.values(compositeSegmentDeletes).reduce((acc, curr) => {
        return acc + (curr.bounds[1] - curr.bounds[0]);
      }, 0),
    [compositeSegmentDeletes]
  );

  useEffect(() => {
    if (compositeSegmentId !== compositeSegment.id) {
      setTimeChangeDueToDrag(0);
      return;
    }
    const currentTotalDeleteTime = Object.values(compositeSegmentFinalDeletes).reduce((acc, curr) => {
      return acc + (curr.bounds[1] - curr.bounds[0]);
    }, 0);

    setTimeChangeDueToDrag(previousTotalDeleteTime - currentTotalDeleteTime);
  }, [compositeSegment.id, compositeSegmentFinalDeletes, compositeSegmentId, finalDeletes, previousTotalDeleteTime]);

  return convertSecondsToTimeString(Math.round(duration + timeChangeDueToDrag || 0), 'HH:mm:ss');
}
